import { getId } from '@mimir/client-common/model/ongoingDownload';
import type { ConstructedOngoingDownload, DownloadReporter, OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import { TaskUrgency } from '@mimir/common/async/taskPriority';
import { PrioritySemaphore, makePriority } from '@mimir/common/async/prioritySemaphore';
import { TransferStateDto, type MonitoredTransfer } from '@mimir/common/dto/transferRequestDtos';
import { getBasenameWithoutExtension } from '@mimir/common/platformAbstraction/pathComposition';
import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import { doesPathExist } from '@mimir/common/util/fs';
import { ConsoleLogger } from '@mimir/common/util/logging';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { refreshPremiereClipInfos } from '../premiere/syncing';
import { getTransferRequests } from '../util/api';
import { isInPremiere, type ApplicationContext, type AdobePanelContext, type ElectronContext, isInElectron } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import { exceptionToString } from '@mimir/common/util/exception';
import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { executeBatchImportUsingSemaphore } from '../premiere/executeBatchImportUsingSemaphore';
import { tryImport } from '../premiere/tryImport';
import { getPossibleDestinationPaths } from '@mimir/client-common/fileSync/downloadDestinationUtils';


async function processTransfers(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string | undefined,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  transfers: MonitoredTransfer[],
) {
  services.ongoingDownloadOps.mapDownloadProgress(transfers);

  const transfersMap: Record<string, MonitoredTransfer> = keyBy(transfers, (t: MonitoredTransfer) => {
    return getId({ artifactReference: { type: 'ImplicitReference'}, ...t });
  });

  let unhandledError: unknown = null;

  /**
   * Remove finished transfers, if they are not in the server provided transfer list
   * then we are assuming they are finished
   */
  const odIds = services.ongoingDownloadOps.getOngoingDownloadKeys();
  for (const odId of odIds) {
    const ongoingDownload = services.ongoingDownloadOps.getOngoingDownload(odId);
    if (!ongoingDownload || ongoingDownload.yetToBeSentToServer || !ongoingDownload.id) {
      // don't clean up the ones that are just placeholder for the UI to reflect state but not sent to server yet
      continue;
    }

    if (transfersMap[odId]) {
      continue;
    }

    try {
      if (isInPremiere(services.applicationContext) || isInElectron(services.applicationContext)) {
        const userDataSystemPath = config.userDataSystemPath;
        invariant(userDataSystemPath !== undefined, 'userDataSystemPath should be defined when in Premiere or Electron context');
        invariant(services.localStateOps, 'localStateOps should be defined when in Premiere or Electron context');

        await finalizeDownload({
          applicationContext: services.applicationContext,
          notificationOps: services.notificationOps,
          logger: services.logger,
          localStateOps: services.localStateOps,
        }, { ...config, userDataSystemPath }, ongoingDownload);
      }
    } catch (ex) {
      if (ongoingDownload.reporters && ongoingDownload.reporters.length) {
        for (const reporter of ongoingDownload.reporters ?? []) {
          reporter.onReject(ex);
        }
      } else {
        unhandledError = ex;
      }
      continue;
    } finally {
      // before deleting the ongoingDownload, refresh premiere state to reflect file availability
      if (isInPremiere(services.applicationContext)) {
        invariant(services.localStateOps, 'localStateOps should be defined inside Premiere context');
        await refreshPremiereClipInfos({
          applicationContext: services.applicationContext,
          notificationOps: services.notificationOps,
          logger: services.logger,
          ongoingDownloadOps: services.ongoingDownloadOps,
          localStateOps: services.localStateOps,
        }, config.skippedFileExtensions);
      }
      services.ongoingDownloadOps.deleteFromOngoingDownloads(odId);
    }

    for (const reporter of ongoingDownload.reporters ?? []) {
      reporter.onSuccess();
    }
  }

  if (unhandledError) {
    throw unhandledError;
  }
}


/**
 * Perform or trigger any follow-up work needed after an ongoing download ends.
 *
 * When this function is called, we can assume that the server backend and
 * storage managers have done all they intend to do in order to make the
 * requested file available. We can also expect that once this function ends,
 * either by returning or by throwing an exception, the ended download will
 * unconditionally be removed from the set of ongoing downloads.
 *
 * However we cannot assume that the file is visible on the file system at the
 * point where this function is called.
 *
 * In addition to permanent problems such as wrong file system mounts, or
 * incorrectly configured local paths, the file system in question might not be
 * atomic, meaning that even if the Kelda or the storage manager believes that
 * the file has been provided on the file system, the emergence of the file on
 * the local file system may be delayed.
 *
 * Before we commence with any follow-up work, we must therefore verify that the
 * file can be seen locally, so that all subsequent code can take the local
 * availability of the file as a given.
 */
async function finalizeDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  endedDownload: Partial<ConstructedOngoingDownload>,
) {
  const candidatePaths = getPossibleDestinationPaths(endedDownload.downloadDestination);

  let localPath: string | undefined;

  if (candidatePaths.length == 0) {
    /*
      TODO: Figure out why we pass in a Partial ConstructedOngoingDownload where
      we cannot be certain that any local path exists.
    */
    services.logger.warn(`Unexpected ongoing download with no expected paths ${JSON.stringify(endedDownload)}`);
  } else {
    localPath = await waitForLocallyAvailableFile(services.applicationContext, candidatePaths, endedDownload.reporters ?? []);
  }

  await executeImport(services, config, endedDownload);

  const isBatchImport = !isEmpty(endedDownload.batchImportId);
  const suffix = localPath ? ` to "${localPath}"` : '';
  // Suppress these toast message if this is a part of batch import
  if (!isInPremiere(services.applicationContext) && !isBatchImport) {

    let filename = endedDownload.originalFileName;
    let description = '';
    if (endedDownload.artefact === 'proxy') {
      description = ' (proxy)';
    } else if (endedDownload.artefact === 'aaf') {
      const simpleName = getBasenameWithoutExtension(filename!, services.applicationContext.pathSep);
      filename = `${simpleName}.aaf`;
    }

    showToast(`Successfully downloaded ${filename}${description}${suffix}`, TOAST.SUCCESS);
  }
}


/**
 * Waits up to a minute for a file to become available at any of the candidate
 * paths. Keeps the download reporters informed about what is going on. Throws
 * an error if the file doesn't show up within one minute.
 */
async function waitForLocallyAvailableFile(applicationContext: AdobePanelContext | ElectronContext, candidatePaths: string[], reporters: DownloadReporter[]): Promise<string> {
  const logger = new ConsoleLogger();
  const deadline = Date.now() + 1000 * 60;
  do {
    const before = Date.now();
    for (const path of candidatePaths) {
      const exists = await doesPathExist(applicationContext.nodeApi, logger, path, false);
      if (exists) {
        return path;
      }
    }
    const after = Date.now();

    let msg = 'Waiting for filesystem to make downloaded file available.';
    if (after - before > 1000) {
      msg += ' File system is slow to respond.';
    }
    for (const reporter of reporters) {
      reporter.onActivity(msg);
    }
    await asyncTimeout(1000);
  } while (Date.now() < deadline);

  const pathDesc = [...candidatePaths].join(' or ');
  throw new Error('File did not arrive at ' + pathDesc + ' within one minute of transfer ending.');
}


const POLL_DURATION = 5;
let transferProgressTimerId: any = null;
const pollingSemaphore = new PrioritySemaphore(1);


export function startPollingTransferProgress(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string | undefined,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  storageId: string,
) {
  if (storageId === PREMIERE_STANDALONE_STORAGE_ID) {
    return;
  }
  if (transferProgressTimerId) {
    services.logger.debug(`Already polling a transfers' progress for: ${storageId}`);
  } else {
    services.logger.debug(`Starting download progress polling for: ${storageId}`);
    transferProgressTimerId = setInterval(() => void pollAndProcessTransferProgressSingly(services, config, storageId), POLL_DURATION * 1000);
  }
}


async function pollAndProcessTransferProgressSingly(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string | undefined,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  storageId: string,
) {
  await pollingSemaphore.limit(() => pollAndProcessTransferProgress(services, config, storageId), makePriority({ urgency: TaskUrgency.LongRunning }), null);
}


async function pollAndProcessTransferProgress(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string | undefined,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  storageId: string,
) {
  try {
    const transfers = await getTransferRequests(storageId, true);
    await processTransfers(services, config, transfers);
    if (canPollingBeStopped(transfers)) {
      stopPollingTransferProgress(services.logger, storageId);
    }
  } catch (error) {
    services.logger.error(`Error in pollAndProcessTransferProgress. storage: ${storageId} ${exceptionToString(error)}`);
  }
}


export function stopPollingTransferProgress(logger: Logger, storageId: string = '') {
  logger.debug(`Stopping transfer progress polling: %c${JSON.stringify(storageId)}`);
  clearInterval(transferProgressTimerId);
  transferProgressTimerId = undefined;
}


function canPollingBeStopped(ongoingDownloads: MonitoredTransfer[]) {
  return isEmpty(ongoingDownloads) ||
         isEmpty(ongoingDownloads.filter(od => od.transferState !== TransferStateDto.ERROR));
}


async function executeImport(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
  },
  endedDownload: OngoingDownload,
) {
  const isBatchImport = !isEmpty(endedDownload.batchImportId);
  if (isBatchImport) {
    await executeBatchImportUsingSemaphore(services, config, endedDownload.batchImportId!);
  } else {
    await tryImport(services, config, endedDownload, undefined);
  }
}
